import React, { useState, useEffect, useRef } from 'react';
import { useT } from '../i18n.js';
// ⚠ MODIFIED FROM PORT — file-upload helper for ScreenSubmit
import { api, fileToBase64, downloadInvoiceDocument, downloadInvoiceAttachment, formatBytes } from '../api.js';
// 2026-09-16 — one-file-per-request uploads + photo compression (see upload.js)
import { uploadFiles } from '../upload.js';
// ⚠ MODIFIED FROM PORT — browser-local "save draft" for the submit-invoice form
import { useFormDraft, savedAgo } from '../drafts.js';
import { BILLING_STATUS_META, MY_BILLS, fmtTHB, netPayableOf, parseMoney } from './data.jsx';
import { MoneyInput } from './money-input.jsx';
import { IconArrow, IconBank, IconBuilding, IconCal, IconChart, IconCheck, IconChevron, IconClock, IconDots, IconDownload, IconExt, IconFile, IconLock, IconLogout, IconPlus, IconReceipt, IconSearch, IconUpload, IconX } from './icons.jsx';
import { Avatar, StatusPill, DraftRestoreBanner } from './screens-internal.jsx';
import { EditInvoiceModal } from './modals.jsx';

import { useState as useStateP } from 'react';


/* ============================================================
   SCREEN 7 — Submit billing (file upload + form)
   ============================================================ */
// ⚠ MODIFIED FROM PORT — wizard / OCR-mock UI replaced with a controlled
// form that actually submits an invoice. Restore the richer UI in Phase 4.
// Pristine invoice form — also the blank-baseline the draft layer compares
// against to decide whether there's anything worth saving.
const SUBMIT_DEFAULTS = {
  po_number: '', project: '', invoice_date: '', due_at: '',
  amount_thb: '', vat_thb: '', wht_thb: '', note: '',
};

export function ScreenSubmit({ onCancel, onSubmit, vendor, draftKey }) {
  const t = useT();
  const [form, setForm] = useState(SUBMIT_DEFAULTS);
  // ⚠ MODIFIED FROM PORT — browser-local draft (text fields only; the invoice
  // PDF and proof-of-performance files are re-attached on resume).
  const { pendingDraft, restore, discard, saveNow, clear: clearDraft, savedAt } =
    useFormDraft({ key: draftKey, form, setForm, defaults: SUBMIT_DEFAULTS });
  const [file, setFile] = useState(null);
  const [popFiles, setPopFiles] = useState([]);   // proof-of-performance files
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null);   // { index, total, name, phase } while uploading
  const [error, setError] = useState(null);
  // The error box sits at the bottom of the form, below the fold on most
  // screens, while the submit button is at the top. Without scrolling to it
  // a failed submit looks like the button did nothing. The sequence number
  // re-scrolls when the same message comes back on a second click.
  const [errorSeq, setErrorSeq] = useState(0);
  const errorRef = useRef(null);
  const showError = (msg) => { setError(msg); setErrorSeq(n => n + 1); };
  useEffect(() => {
    if (errorSeq) errorRef.current?.scrollIntoView({ block: 'center' });
  }, [errorSeq]);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const POP_MAX = 5;
  const POP_MAX_BYTES = 20 * 1024 * 1024;
  const POP_ACCEPT = 'image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx';

  const addPopFiles = (incoming) => {
    if (!incoming || !incoming.length) return;
    setPopFiles(prev => {
      const next = [...prev];
      for (const f of incoming) {
        if (next.length >= POP_MAX) break;
        // De-dupe by name+size so re-selecting the same file is a no-op
        if (next.some(x => x.name === f.name && x.size === f.size)) continue;
        next.push(f);
      }
      return next;
    });
  };
  const removePopFile = (idx) => setPopFiles(prev => prev.filter((_, i) => i !== idx));

  // Every field below is mandatory. Empty string fails the check.
  const validate = () => {
    // Labels match the form's own field labels so the Thai message names
    // the field the vendor actually sees.
    const required = [
      ['po_number',     'Invoice number'],
      ['project',       'Project'],
      ['invoice_date',  'Invoice date'],
      ['due_at',        'Payment due date'],
      ['amount_thb',    'Amount THB'],
      ['vat_thb',       'VAT THB'],
      ['wht_thb',       'Withholding tax THB'],
    ];
    for (const [k, label] of required) {
      const v = String(form[k] || '').trim();
      if (!v) return t('{field} is required.', { field: t(label) });
    }
    // Money fields are free text (see MoneyInput) — parse, don't Number().
    if (!(parseMoney(form.amount_thb) > 0)) return t('Amount must be a number greater than zero (e.g. 45000 or 45,000.00).');
    if (!Number.isFinite(parseMoney(form.vat_thb))) return t('VAT must be a number (enter 0 if not applicable).');
    if (!Number.isFinite(parseMoney(form.wht_thb))) return t('Withholding tax must be a number (enter 0 if not applicable).');
    if (!file) return t('Invoice document (PDF) is required.');
    if (popFiles.length < 1) return t('At least one proof-of-performance file is required (site photo, delivery note, etc).');
    if (popFiles.length > POP_MAX) return t('Maximum {n} proof-of-performance files.', { n: POP_MAX });
    for (const f of popFiles) {
      if (f.size > POP_MAX_BYTES) return t('{name}: must be ≤ 20 MB.', { name: f.name });
    }
    return null;
  };

  const submit = async () => {
    setError(null);
    const v = validate();
    if (v) { showError(v); window.__toast?.(v, 'error'); return; }
    setSubmitting(true);
    try {
      let payload = {
        ...form,
        amount_thb: parseMoney(form.amount_thb),
        vat_thb:    parseMoney(form.vat_thb),
        wht_thb:    parseMoney(form.wht_thb),
      };
      if (file.size > 20 * 1024 * 1024) throw new Error(t("PDF must be ≤ 20MB"));
      // Each file goes up on its own request (photos compressed first); the
      // submit then carries Drive ids only. Keeps phone memory flat and means
      // one slow file can't take the whole submission down with it.
      const total = 1 + popFiles.length;
      const [pdfMeta] = await uploadFiles([file], 'invoice-pdf', p => setProgress({ ...p, index: 1, total }));
      const popMeta = await uploadFiles(popFiles, 'pop', p => setProgress({ ...p, index: 1 + p.index, total }));
      setProgress({ phase: 'submit', index: total, total });
      payload.pdf_file_id  = pdfMeta.id;
      payload.pdf_filename = pdfMeta.name;
      payload.pop_file_ids = popMeta.map(m => m.id);
      await onSubmit?.(payload);
      clearDraft();          // submitted — drop the saved draft
    } catch (e) {
      showError(e.message);
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  const onSaveDraft = () => {
    if (saveNow()) window.__toast?.(t('Draft saved'), 'success');
  };

  const req = <span style={{color:"var(--danger)"}}>*</span>;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>{t("Submit invoice")}</h1>
          <div className="sub">{vendor ? t("Submitting as {name}", { name: vendor.name }) : t('Routes to PM review automatically')}</div>
        </div>
        <div className="page-actions">
          {savedAt && !pendingDraft && (
            <span className="muted" style={{fontSize: 11, alignSelf: "center", marginRight: 4, whiteSpace: "nowrap"}}>
              {t("Draft saved · {when}", { when: savedAgo(savedAt, t) })}
            </span>
          )}
          <button className="btn btn-ghost btn-sm" onClick={onSaveDraft} disabled={submitting}>{t("Save draft")}</button>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={submitting}>{t("Cancel")}</button>
          <button className="btn btn-primary btn-sm" onClick={submit} disabled={submitting}>
            <IconCheck size={14}/> {submitting
              ? (progress && progress.phase !== 'submit'
                  ? t('Uploading {n} of {total}…', { n: progress.index, total: progress.total })
                  : t('Submitting…'))
              : t('Submit for review')}
          </button>
        </div>
      </div>

      {pendingDraft && (
        <DraftRestoreBanner t={t} savedAt={pendingDraft.savedAt} onRestore={restore} onDiscard={discard}/>
      )}

      <div style={{padding: "8px 24px 24px", display: "grid", gridTemplateColumns: "1fr 320px", gap: 24}}>
        <div style={{display:"flex", flexDirection:"column", gap: 16}}>
          <div className="section">
            <div className="section-head"><h3>{t("Invoice document")} {req}</h3>
              <span className="muted" style={{fontSize: 12}}>{t("PDF only · max 20 MB")}</span>
            </div>
            <div className="section-body">
              <input type="file" accept="application/pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={{ display: 'block', padding: 8 }}/>
              {file && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-tertiary)' }}>
                  {file.name} · {(file.size / 1024).toFixed(0)} KB
                </div>
              )}
            </div>
          </div>

          <div className="section">
            <div className="section-head"><h3>{t("Proof of performance")} {req}</h3>
              <span className="muted" style={{fontSize: 12}}>
                {popFiles.length}/{POP_MAX} · {t("photos · PDFs · Office docs · max 20 MB each")}
              </span>
            </div>
            <div className="section-body">
              <input type="file" multiple accept={POP_ACCEPT}
                onChange={(e) => { addPopFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
                disabled={popFiles.length >= POP_MAX}
                style={{ display: 'block', padding: 8 }}/>
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {t("Attach evidence that the service was actually delivered — site photos, delivery notes, signed proof-of-delivery, completion reports. The PM and Finance reviewers will see these when approving your invoice.")}
              </div>
              {popFiles.length > 0 && (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {popFiles.map((f, i) => (
                    <div key={`${f.name}-${i}`} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 10px', background: 'var(--surface-2, #f7f5fc)',
                      border: '1px solid var(--border-subtle)', borderRadius: 6,
                    }}>
                      <span style={{
                        width: 28, height: 28, borderRadius: 4,
                        background: 'var(--accent-soft, #efe9fc)', color: 'var(--accent, #6200FF)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                      }}>
                        {(f.name.split('.').pop() || '?').slice(0, 4)}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, color: 'var(--text-primary)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{f.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                          {(f.size / 1024).toFixed(0)} KB
                        </div>
                      </div>
                      <button type="button" className="btn btn-ghost btn-sm"
                        onClick={() => removePopFile(i)}
                        style={{ color: 'var(--danger)', padding: '4px 8px' }}>
                        {t("Remove")}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="section">
            <div className="section-head"><h3>{t("Invoice details")}</h3>
              <span className="muted" style={{fontSize: 12}}>{t("All fields required")}</span>
            </div>
            <div className="section-body">
              <div className="form-grid">
                <div className="field">
                  <label className="field-label">{t("Invoice number")} {req}</label>
                  <input className="input" value={form.po_number} onChange={set('po_number')} placeholder={t("e.g. your invoice no.")}/>
                </div>
                <div className="field">
                  <label className="field-label">{t("Project")} {req}</label>
                  <input className="input" value={form.project} onChange={set('project')} placeholder="Toyota Bz4X campaign"/>
                </div>
                <div className="field">
                  <label className="field-label">{t("Invoice date")} {req}</label>
                  <input className="input" type="date" value={form.invoice_date} onChange={set('invoice_date')}
                    onClick={(e) => e.currentTarget.showPicker?.()}/>
                  <span className="field-help">{t("The date printed on your invoice document.")}</span>
                </div>
                <div className="field">
                  <label className="field-label">{t("Payment due date")} {req}</label>
                  <input className="input" type="date" value={form.due_at} onChange={set('due_at')}
                    onClick={(e) => e.currentTarget.showPicker?.()}/>
                </div>
                <div className="field">
                  <label className="field-label">{t("Amount THB")} <span className="muted" style={{fontWeight: 600, color: "var(--text-accent)"}}>({t("Ex-VAT")})</span> {req}</label>
                  <MoneyInput value={form.amount_thb} onChange={set('amount_thb')} placeholder="178,880.00"/>
                  <span className="field-help">{t("Pre-VAT (net of VAT) amount. Net payable = Amount + VAT − Withholding tax.")}</span>
                </div>
                <div className="field">
                  <label className="field-label">{t("VAT THB")} {req}</label>
                  <MoneyInput value={form.vat_thb} onChange={set('vat_thb')} placeholder="12,521.60"/>
                  <span className="field-help">{t("Enter 0 if not applicable.")}</span>
                </div>
                <div className="field">
                  <label className="field-label">{t("Withholding tax THB")} {req}</label>
                  <MoneyInput value={form.wht_thb} onChange={set('wht_thb')} placeholder="5,366.40"/>
                  <span className="field-help">{t("Enter 0 if not applicable.")}</span>
                </div>
                <div className="field full">
                  <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 14px", background: "var(--bg-secondary)", border: "1px solid var(--border-subtle)", borderRadius: 8}}>
                    <span className="muted" style={{fontSize: 12}}>
                      {t("Net payable")} <span style={{textTransform: "none", letterSpacing: 0}}>({t("Amount + VAT − Withholding tax")})</span>
                    </span>
                    <span className="tnum" style={{fontSize: 16, fontWeight: 600, color: "var(--text-primary)"}}>
                      {fmtTHB((parseMoney(form.amount_thb) || 0) + (parseMoney(form.vat_thb) || 0) - (parseMoney(form.wht_thb) || 0))}
                    </span>
                  </div>
                </div>
                <div className="field full">
                  <label className="field-label">{t("Note to MotionReach")} <span className="muted">({t("optional")})</span></label>
                  <textarea className="input" rows={3} value={form.note} onChange={set('note')}
                    placeholder={t("Anything we should flag for the reviewer?")}/>
                </div>
              </div>
              {error && (
                <div ref={errorRef} style={{margin: "12px 18px 0", padding: "10px 12px", background: "color-mix(in oklab, var(--danger) 12%, transparent)", color: "var(--danger)", borderRadius: 8, fontSize: 13}}>
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{display:"flex", flexDirection:"column", gap: 16}}>
          <div className="section">
            <div className="section-head"><h3>{t("Vendor")}</h3></div>
            <div className="section-body">
              <dl className="kv" style={{gridTemplateColumns: "100px 1fr"}}>
                <dt>{t("Name")}</dt><dd>{vendor?.name || "—"}</dd>
                <dt>{t("Vendor ID")}</dt><dd className="mono">{vendor?.vendor_id || "—"}</dd>
                <dt>{t("Terms")}</dt><dd className="mono">{vendor?.terms || "—"}</dd>
              </dl>
            </div>
          </div>
          <div className="section">
            <div className="section-head"><h3>{t("What happens next")}</h3></div>
            <div className="section-body" style={{fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6}}>
              {t("On submit, your invoice is created in pm-review status. The MotionReach PM verifies it against the PO, then Finance verifies tax + bank details, then it's scheduled for payment per your credit terms.")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ============================================================
   SCREEN 8 — Supplier portal billing status tracker
   ============================================================ */
export function ScreenPortalBilling({ onSubmit, invoices, onRefresh }) {
  const t = useT();
  // ⚠ MODIFIED FROM PORT — accept live invoices; fall back to MY_BILLS mock.
  const allBills = invoices && invoices.length ? invoices : (invoices ? [] : MY_BILLS);
  const [tab, setTab] = useStateP("all");
  const [query, setQuery] = useStateP("");
  // ⚠ NEW (2026-06-17) — supplier can edit an invoice while it's still in pm-review.
  const [editing, setEditing] = useStateP(null);
  const tabCounts = {
    all:      allBills.length,
    review:   allBills.filter(b => ["pm-review","finance-verify"].includes(b.status)).length,
    approved: allBills.filter(b => ["approved-payment","scheduled"].includes(b.status)).length,
    paid:     allBills.filter(b => b.status === "paid").length,
    needs:    allBills.filter(b => ["disputed","rejected"].includes(b.status)).length,
  };
  const bills = (() => {
    let out = allBills;
    if (tab === "review")   out = out.filter(b => ["pm-review","finance-verify"].includes(b.status));
    else if (tab === "approved") out = out.filter(b => ["approved-payment","scheduled"].includes(b.status));
    else if (tab === "paid") out = out.filter(b => b.status === "paid");
    else if (tab === "needs") out = out.filter(b => ["disputed","rejected"].includes(b.status));
    if (query) {
      const q = query.toLowerCase();
      out = out.filter(b =>
        String(b.id || '').toLowerCase().includes(q) ||
        String(b.po || '').toLowerCase().includes(q) ||
        String(b.project || '').toLowerCase().includes(q)
      );
    }
    return out;
  })();
  const [openIdx, setOpenIdx] = useStateP(0);

  if (!bills.length) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-title-group">
            <h1>{t("My billing")}</h1>
            <div className="sub">{t("No invoices submitted yet")}</div>
          </div>
          <div className="page-actions">
            <button className="btn btn-primary btn-sm" onClick={onSubmit}><IconPlus size={14}/> {t("New invoice")}</button>
          </div>
        </div>
        <div style={{ padding: 48, textAlign: "center", color: "var(--text-tertiary)" }}>
          {t("Submit your first invoice to MotionReach to see it appear here.")}
        </div>
      </div>
    );
  }

  const open = bills[Math.max(0, Math.min(openIdx, bills.length - 1))];
  // ⚠ MODIFIED FROM PORT — strip staff actor names from supplier-facing
  // progress timeline. Suppliers see roles, not individual reviewers.
  const submittedAt = open.submitted ? new Date(open.submitted).toLocaleString() : '';
  const stages = [
    { key: "submitted",       label: "Submitted",      when: submittedAt },
    { key: "pm-review",       label: "PM review",      when: null },
    { key: "finance-verify",  label: "Finance verify", when: null },
    { key: "approved-payment",label: "Approved",       when: null },
    { key: "scheduled",       label: "Scheduled",      when: null },
    { key: "paid",            label: "Paid",           when: null },
  ];
  // Translated when rendering via t(st.label) below.
  // Determine progress based on status
  const order = ["submitted","pm-review","finance-verify","approved-payment","scheduled","paid"];
  const progressIdx = order.indexOf(open.status);

  // Totals
  const totals = bills.reduce((acc, b) => {
    if (b.status === "paid") acc.paid += netPayableOf(b);
    else if (b.status === "approved-payment" || b.status === "scheduled") acc.approved += netPayableOf(b);
    else if (b.status === "rejected" || b.status === "disputed") acc.disputed += netPayableOf(b);
    else acc.pending += netPayableOf(b);
    return acc;
  }, { paid: 0, approved: 0, pending: 0, disputed: 0 });
  // ⚠ Real counts + avg pay cycle (was hardcoded captions)
  const cnt = {
    pending:  bills.filter(b => ["pm-review","finance-verify"].includes(b.status)).length,
    approved: bills.filter(b => ["approved-payment","scheduled"].includes(b.status)).length,
    paid:     bills.filter(b => b.status === "paid").length,
    disputed: bills.filter(b => ["disputed","rejected"].includes(b.status)).length,
  };
  const paidCycles = bills
    .filter(b => b.status === "paid" && b.submitted && b.paid_at)
    .map(b => (new Date(b.paid_at) - new Date(b.submitted)) / 86400000)
    .filter(x => x >= 0);
  const avgPaidCycle = paidCycles.length ? (paidCycles.reduce((a,b) => a+b, 0) / paidCycles.length).toFixed(1) : null;
  const plural = (n) => n === 1 ? '' : 's';

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>{t("My billing")}</h1>
          <div className="sub">{t("All invoices submitted to MotionReach · synced just now")}</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary btn-sm" onClick={onSubmit}><IconPlus size={14}/> {t("New invoice")}</button>
        </div>
      </div>

      <div className="stat-row">
        <div className="stat-tile">
          <div className="label">{t("Awaiting review")}</div>
          <div className="val tnum">{fmtTHB(totals.pending)}</div>
          <div className="delta">{t("{n} in progress", { n: cnt.pending })}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t("Approved · awaiting pay")}</div>
          <div className="val tnum" style={{color: "var(--info)"}}>{fmtTHB(totals.approved)}</div>
          <div className="delta">{t("{n} scheduled", { n: cnt.approved })}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t("Paid")}</div>
          <div className="val tnum">{fmtTHB(totals.paid)}</div>
          <div className="delta">{t("{n} paid", { n: cnt.paid })}{avgPaidCycle ? t(" · avg {d} days", { d: avgPaidCycle }) : ''}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t("Disputed / rejected")}</div>
          <div className="val tnum" style={{color: "var(--danger)"}}>{fmtTHB(totals.disputed)}</div>
          <div className="delta down">{t("{n} need your input", { n: cnt.disputed })}</div>
        </div>
      </div>

      <div style={{display: "grid", gridTemplateColumns: "1fr 440px", flex: 1, overflow: "hidden", borderTop: "1px solid var(--border-subtle)"}}>
        <div style={{overflowY:"auto"}}>
          <div className="toolbar" style={{borderTop: "none"}}>
            {[
              ["all",       "All",            tabCounts.all],
              ["review",    "In review",      tabCounts.review],
              ["approved",  "Approved",       tabCounts.approved],
              ["paid",      "Paid",           tabCounts.paid],
              ["needs",     "Needs attention",tabCounts.needs],
            ].map(([id, lbl, n]) => (
              <button key={id} className={"chip " + (tab === id ? "active" : "")} onClick={() => setTab(id)}>
                {t(lbl)} <span className="muted">{n}</span>
              </button>
            ))}
            <div className="toolbar-right">
              <div className="topbar-search" style={{maxWidth: 200, margin: 0}}>
                <IconSearch size={14} className="icon"/>
                <input placeholder={t("Search invoices…")} value={query} onChange={e => setQuery(e.target.value)}/>
              </div>
            </div>
          </div>

          <table className="tbl">
            <colgroup>
              <col style={{width: 160}}/>
              <col style={{width: 130}}/>
              <col/>
              <col style={{width: 120}}/>
              <col style={{width: 100}}/>
              <col style={{width: 160}}/>
              <col style={{width: 36}}/>
            </colgroup>
            <thead>
              <tr>
                <th>{t("Invoice")}</th>
                <th>{t("Invoice no.")}</th>
                <th>{t("Project")}</th>
                <th className="right">{t("Amount")}</th>
                <th>{t("Submitted")}</th>
                <th>{t("Status")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b, i) => (
                <tr key={b.id}
                  onClick={() => setOpenIdx(i)}
                  className={i === openIdx ? "selected" : ""}
                  style={{cursor: "pointer"}}>
                  <td className="mono" style={{color: "var(--text-primary)"}}>{b.id}</td>
                  <td className="mono muted">{b.po}</td>
                  <td className="ellipsis">{b.project}</td>
                  <td className="num right tnum">{fmtTHB(netPayableOf(b))}</td>
                  <td className="muted" style={{fontSize: 12}}>{b.submitted}</td>
                  <td><StatusPill meta={BILLING_STATUS_META[b.status]}/></td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{borderLeft: "1px solid var(--border-subtle)", background: "var(--bg-secondary)", overflowY: "auto", padding: 20}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom: 16}}>
            <div>
              <div style={{fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.08em"}}>{t("Invoice")}</div>
              <div style={{fontFamily: "var(--font-mono)", fontSize: 16, fontWeight: 500, marginTop: 4}}>{open.id}</div>
            </div>
            <div style={{display: "flex", alignItems: "center", gap: 8}}>
              {open.status === "pm-review" && (
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(open)}>{t("Edit")}</button>
              )}
              <StatusPill meta={BILLING_STATUS_META[open.status]}/>
            </div>
          </div>
          {open.status === "pm-review" && (
            <div style={{marginTop: -8, marginBottom: 12, fontSize: 11, color: "var(--text-tertiary)"}}>
              {t("You can still edit this invoice until MotionReach starts reviewing it.")}
            </div>
          )}

          <div style={{
            background: "var(--bg-primary)",
            borderRadius: "var(--radius-md)",
            padding: 16,
            border: "1px solid var(--border-subtle)",
            marginBottom: 16,
          }}>
            <div className="muted" style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em"}}>{t("Amount due")}</div>
            <div style={{fontSize: 30, fontWeight: 600, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums", marginTop: 4}}>
              {fmtTHB(netPayableOf(open))}
            </div>
            <div style={{marginTop: 4, fontSize: 12, color: "var(--text-tertiary)"}}>
              {t("Inclusive of 7% VAT · WHT 3% withheld")}
            </div>
            <div style={{marginTop: 6, fontSize: 12, color: "var(--text-tertiary)"}}>
              {t("Project")}: <span style={{color: "var(--text-secondary)"}}>{open.project}</span>
            </div>
            <div style={{marginTop: 2, fontSize: 12, color: "var(--text-tertiary)"}}>
              {t("Invoice no.")}: <span className="mono" style={{color: "var(--text-secondary)"}}>{open.po}</span> · {t("Due")}: <span className="mono" style={{color: "var(--text-secondary)"}}>{open.due}</span>
            </div>
          </div>

          <div style={{fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12}}>{t("Progress")}</div>
          <div className="timeline">
            {stages.map((st, i) => {
              const done = i <= progressIdx;
              const current = i === progressIdx + 1;
              const cls = done ? "done" : (current ? "" : "pending");
              return (
                <div key={st.key} className="tl-item">
                  <div className="tl-marker"><span className={"dot " + cls}></span></div>
                  <div className="tl-body">
                    <div className="when">{st.when || (done ? "" : current ? t("in progress") : "—")}</div>
                    <div className="what" style={{color: done || current ? "var(--text-primary)" : "var(--text-tertiary)"}}>{t(st.label)}</div>
                    {st.actor && <div className="who">{st.actor}</div>}
                  </div>
                </div>
              );
            })}
          </div>

          {open.status === "disputed" && (
            <div style={{
              marginTop: 16,
              padding: 14,
              background: "color-mix(in oklab, var(--warning) 14%, transparent)",
              borderRadius: "var(--radius-md)",
              borderLeft: "3px solid var(--warning)",
            }}>
              <div style={{fontSize: 13, fontWeight: 400, marginBottom: 4, color: "var(--warning)"}}>{t("Dispute opened — needs your input")}</div>
              <div style={{fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5}}>
                {t("MotionReach Finance flagged a discrepancy on this invoice. Contact your MotionReach representative for details.")}
              </div>
            </div>
          )}

          <div className="hr"></div>

          {/* ⚠ MODIFIED FROM PORT — real attachment + wired download */}
          <InvoiceAttachmentPortal invoice={open}/>

          {/* ⚠ NEW (2026-07-27) — once paid, the supplier uploads the tax
              invoice (ใบกำกับภาษี) here; Finance downloads it internally. */}
          {open.status === "paid" && (
            <>
              <div className="hr"></div>
              <TaxInvoiceSectionPortal invoice={open} onRefresh={onRefresh}/>
            </>
          )}
        </div>
      </div>

      <EditInvoiceModal
        open={!!editing}
        invoice={editing}
        mode="supplier"
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); onRefresh?.(); }}
      />
    </div>
  );
}

/* ⚠ NEW (2026-07-27) — supplier uploads the tax invoice / official receipt for
 * a PAID invoice. Appends to any earlier upload (add the receipt later), max 5
 * files total; same 20MB + file-type rules as proof-of-performance uploads. */
const TAX_ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif,.doc,.docx,.xls,.xlsx,.ppt,.pptx";
const TAX_MAX_FILES = 5;
const TAX_MAX_BYTES = 20 * 1024 * 1024;

function TaxInvoiceSectionPortal({ invoice, onRefresh }) {
  const t = useT();
  const [busy, setBusy] = useStateP(false);
  const [error, setError] = useStateP('');
  const [justUploaded, setJustUploaded] = useStateP(false);
  const inputRef = React.useRef(null);
  const invoiceId = invoice?.invoice_id || invoice?.id;
  const taxFiles = Array.isArray(invoice?.tax_attachments) ? invoice.tax_attachments : [];
  const remaining = Math.max(0, TAX_MAX_FILES - taxFiles.length);

  if (invoice?.status !== 'paid') return null;

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';   // so re-picking the same file re-fires onChange
    if (!files.length) return;
    if (files.length > remaining) {
      setError(t("Too many files — you can add {n} more (max {max} total).", { n: remaining, max: TAX_MAX_FILES }));
      return;
    }
    const oversize = files.find(f => f.size > TAX_MAX_BYTES);
    if (oversize) { setError(t("{name} is larger than 20MB.", { name: oversize.name })); return; }
    setBusy(true); setError('');
    try {
      // One request per file (photos compressed), then attach by id.
      const metas = await uploadFiles(files, 'tax');
      await api.uploadTaxInvoice(invoiceId, null, metas.map(m => m.id));
      setJustUploaded(true);
      onRefresh?.();
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setBusy(false);
    }
  };

  const download = async (att) => {
    try { await downloadInvoiceAttachment(invoiceId, att.id, att.name); }
    catch (e) { alert('Download failed: ' + e.message); }
  };

  return (
    <>
      <div style={{fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10}}>
        {t("Tax invoice")} {taxFiles.length > 0 && <span style={{color: "var(--text-secondary)", textTransform: "none", letterSpacing: 0}}>· {t("{n} files", { n: taxFiles.length })}</span>}
      </div>

      {taxFiles.length === 0 ? (
        <div style={{
          padding: 14, marginBottom: 12,
          background: "color-mix(in oklab, var(--warning) 14%, transparent)",
          borderRadius: "var(--radius-md)",
          borderLeft: "3px solid var(--warning)",
          fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5,
        }}>
          {t("This invoice is paid — please upload your tax invoice / official receipt for our accounting records.")}
        </div>
      ) : (
        <div style={{display: "flex", flexDirection: "column", gap: 6, marginBottom: 12}}>
          {taxFiles.map(att => (
            <div className="file-row" key={att.id}>
              <div className="file-icon" style={{textTransform: "uppercase"}}>
                {(att.name?.split('.').pop() || '?').slice(0, 4)}
              </div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="file-name" style={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                  {att.name || att.id}
                </div>
                <div className="file-meta">
                  {att.size ? formatBytes(att.size) : '—'} · {t("uploaded by you")}
                </div>
              </div>
              <button className="icon-btn" onClick={() => download(att)} disabled={busy} title={t("Download")}>
                <IconDownload size={14}/>
              </button>
            </div>
          ))}
        </div>
      )}

      {justUploaded && taxFiles.length > 0 && (
        <div style={{fontSize: 12, color: "var(--success)", marginBottom: 10}}>
          {t("Tax invoice uploaded — thank you!")}
        </div>
      )}
      {error && (
        <div style={{fontSize: 12, color: "var(--danger)", marginBottom: 10}}>{error}</div>
      )}

      {remaining > 0 && (
        <>
          <input ref={inputRef} type="file" multiple accept={TAX_ACCEPT}
            style={{display: "none"}} onChange={onFiles}/>
          <button className="btn btn-primary btn-sm" disabled={busy}
            onClick={() => { setError(''); inputRef.current?.click(); }}>
            <IconUpload size={14}/> {busy ? t("Uploading…") : (taxFiles.length ? t("Add more files") : t("Upload tax invoice"))}
          </button>
          <div style={{fontSize: 11, color: "var(--text-tertiary)", marginTop: 8}}>
            {t("PDF or a clear photo · up to {max} files · 20MB each", { max: TAX_MAX_FILES })}
          </div>
        </>
      )}
    </>
  );
}

function InvoiceAttachmentPortal({ invoice }) {
  const t = useT();
  const [busyKey, setBusyKey] = useState(null);
  const invoiceId = invoice?.invoice_id || invoice?.id;
  const popAttachments = Array.isArray(invoice?.pop_attachments) ? invoice.pop_attachments : [];

  if (!invoice?.has_pdf && popAttachments.length === 0) {
    return (
      <>
        <div style={{fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10}}>{t("Attachments")}</div>
        <div style={{padding: 14, color: "var(--text-tertiary)", fontSize: 12, fontStyle: "italic"}}>
          {t("No invoice document on file.")}
        </div>
      </>
    );
  }

  const downloadPdf = async () => {
    setBusyKey('pdf');
    try { await downloadInvoiceDocument(invoiceId); }
    catch (e) { alert('Download failed: ' + e.message); }
    finally { setBusyKey(null); }
  };
  const downloadPop = async (att) => {
    setBusyKey(att.id);
    try { await downloadInvoiceAttachment(invoiceId, att.id, att.name); }
    catch (e) { alert('Download failed: ' + e.message); }
    finally { setBusyKey(null); }
  };

  return (
    <>
      <div style={{fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10}}>{t("Invoice document")}</div>
      {invoice?.has_pdf ? (
        <div className="file-row">
          <div className="file-icon">pdf</div>
          <div style={{flex: 1, minWidth: 0}}>
            <div className="file-name" style={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
              {invoice.pdf_filename || `${invoiceId}.pdf`}
            </div>
            <div className="file-meta">
              {invoice.pdf_size ? formatBytes(invoice.pdf_size) : '—'} · {t("uploaded by you")}
            </div>
          </div>
          <button className="icon-btn" onClick={downloadPdf} disabled={!!busyKey} title={t("Download")}>
            {busyKey === 'pdf' ? '…' : <IconDownload size={14}/>}
          </button>
        </div>
      ) : (
        <div style={{padding: 14, color: "var(--text-tertiary)", fontSize: 12, fontStyle: "italic"}}>
          {t("No invoice document on file.")}
        </div>
      )}

      <div style={{fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.08em", margin: "16px 0 10px"}}>
        {t("Proof of performance")} {popAttachments.length > 0 && <span style={{color: "var(--text-secondary)", textTransform: "none", letterSpacing: 0}}>· {t("{n} files", { n: popAttachments.length })}</span>}
      </div>
      {popAttachments.length === 0 ? (
        <div style={{padding: 14, color: "var(--text-tertiary)", fontSize: 12, fontStyle: "italic"}}>
          {t("No proof-of-performance files on this invoice.")}
        </div>
      ) : (
        <div style={{display: "flex", flexDirection: "column", gap: 6}}>
          {popAttachments.map(att => (
            <div className="file-row" key={att.id}>
              <div className="file-icon" style={{textTransform: "uppercase"}}>
                {(att.name?.split('.').pop() || '?').slice(0, 4)}
              </div>
              <div style={{flex: 1, minWidth: 0}}>
                <div className="file-name" style={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                  {att.name || att.id}
                </div>
                <div className="file-meta">
                  {att.size ? formatBytes(att.size) : '—'} · {t("uploaded by you")}
                </div>
              </div>
              <button className="icon-btn" onClick={() => downloadPop(att)} disabled={!!busyKey} title="Download">
                {busyKey === att.id ? '…' : <IconDownload size={14}/>}
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}


